<script setup lang="ts">
import { computed, ref } from "vue";
import { svgIcon, baseTooltip } from "@/common/components";

interface TabType {
  icon: string;
  label?: string;
  key?: string;
  url?: string;
  active?: boolean;
}

const emit = defineEmits(["selected", "close"]);

const props = withDefaults(
  defineProps<{
    tabs: TabType[];
    active: string;
    size?: string;
    showClose?: boolean;
  }>(),
  {
    active: "",
    size: "2.25rem",
    showClose: false,
  }
);

const tabHover = ref<number | null>(null);

const tabList = computed(() => {
  let found = false;
  const list: TabType[] = [];

  props.tabs?.forEach((tab) => {
    list.push({ ...tab, active: !found });

    if (tab.key === props.active) {
      found = true;
    }
  });

  return list;
});

function selectTab(data: TabType) {
  if (data.url) {
    emit("selected", data.url);
    return;
  }

  if (data.key) {
    emit("selected", data.key);
  }
}
</script>

<template>
	<section class="base-icon-menu">
		<div class="header">
			<div class="menus">
				<template v-for="(tab, index) in tabList" :key="index">
					<template v-if="index != 0">
						<div class="separate" :class="{ active: tab.active }" />
					</template>
					<base-tooltip>
						<template #trigger>
							<div
								class="menu"
								:class="{ active: tab.active }"
								@mouseover="tabHover = index"
								@mouseout="tabHover = null"
								@click="selectTab(tab)"
							>
								<svg-icon :icon="tab.icon" size="90%" />
							</div>
						</template>
						<template v-if="tab.label" #tooltip>
							<div class="label">
								{{ tab.label }}
							</div>
						</template>
					</base-tooltip>
				</template>
			</div>
			<div class="actions">
				<template v-if="showClose">
					<button
						type="button"
						class="btn btn-lg btn-default"
						@click="emit('close')"
					>
						<svg-icon icon="close" size="1.25rem" />
					</button>
				</template>
			</div>
		</div>
		<div class="content">
			<slot name="content"></slot>
		</div>
	</section>
</template>

<style lang="scss" scoped>
$size: v-bind("size");

.base-icon-menu {
  display: flex;
  flex-direction: column;

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .menus {
      display: flex;
      flex-direction: row;
      align-items: center;

      .menu {
        cursor: pointer;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: $size;
        height: $size;
        background: var(--white-off);
        padding: 0.5rem;
        border-radius: 0.5rem;
        color: var(--text-off);

        &.active {
          background: var(--secondary);
          color: var(--white);
        }
      }

      .separate {
        width: 1rem;
        height: 4px;
        background: var(--white-off);

        &.active {
          background: var(--secondary);
        }
      }
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    background: var(--white);
  }
}
</style>
